import {
  QUERY_KEY_NAME_PAGE,
  QUERY_KEY_NAME_SORT,
  QUERY_KEY_NAME_SORT_DIR
} from '../constants';
import type { ProductFiltersModel } from '../model';
import { CATEGORY_ID, SKU } from '~/utils/shop/constants';

/** UI / Magento keys that are never facet aggregations but must stay in filters. */
export const ALWAYS_ALLOWED_FILTER_KEYS = new Set<string>([
  QUERY_KEY_NAME_PAGE,
  QUERY_KEY_NAME_SORT,
  QUERY_KEY_NAME_SORT_DIR,
  SKU,
  CATEGORY_ID
]);

export function mergeAggregationCodes(
  known: ReadonlySet<string>,
  aggregations: ReadonlyArray<{ attribute_code?: string | null } | null>,
  /**
   * Active filter keys to keep across zero-result responses (deeplink).
   * Only seeded when the response carries no aggregation codes — otherwise the
   * aggregations are authoritative and query junk (e.g. ad-click ids landing on
   * the PLP) must never become a "known" facet code.
   */
  activeFilterKeys: ReadonlyArray<string> = []
): Set<string> {
  const aggregationCodes = aggregations.flatMap((a) =>
    a?.attribute_code ? [a.attribute_code] : []
  );
  const seededKeys =
    aggregationCodes.length === 0
      ? activeFilterKeys.filter((key) => !ALWAYS_ALLOWED_FILTER_KEYS.has(key))
      : [];

  return new Set([...known, ...aggregationCodes, ...seededKeys]);
}

/**
 * Drops non-Magento query junk once we know which facet codes exist.
 * Does nothing before the first aggregation merge (empty known set).
 */
export function pruneFiltersToKnownCodes(
  filters: ProductFiltersModel,
  knownFilterCodes: ReadonlySet<string>
): ProductFiltersModel {
  if (knownFilterCodes.size === 0) {
    return filters;
  }

  return Object.fromEntries(
    Object.entries(filters).filter(
      ([key]) =>
        ALWAYS_ALLOWED_FILTER_KEYS.has(key) || knownFilterCodes.has(key)
    )
  );
}
